import { useEffect, useState } from "react";
import { MiChip, MiDetailPageHeader, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { Box, Typography, useTheme } from "@mui/material";
import { StepperView } from "../../../components/StepperView/StepperView";
import { ImportPlanConfig } from "./ImportPlanConfig/ImportPlanConfig";
import { ImportPlanOptions } from "./ImportPlanOptions/ImportPlanOptions";
import { useImportPlanReviewViewModel } from "./useImportPlanReviewViewModel";

const ACTIVE_STEPS = [
  { label: "Configure Bid", value: 0 },
  { label: "Configure Options", value: 1 },
  { label: "Review", value: 2 },
];

const ACTIVE_STEP = {
  CONFIGURE: 0,
  OPTIONS: 1,
  REVIEW: 2,
};

export function ImportPlanReview() {
  const [activeStep, setActiveStep] = useState(ACTIVE_STEP.CONFIGURE);
  const [continueReviewModal, setContinueReviewModal] = useState(false);

  const { params } = useRouter();
  const { planId } = params;
  const {
    isLoading,
    getPlanDetails,
    planDetails,
    areaTypes,
    stateManager,
    setPlanDetails,
    handleUpdateCurrentStep,
  } = useImportPlanReviewViewModel();
  const theme = useTheme();

  useEffect(() => {
    getPlanDetails(planId);
  }, []);

  useEffect(() => {
    if (planDetails.currentStep) {
      setActiveStep(planDetails.currentStep);
    }
  }, [planDetails.currentStep]);

  const title = (
    <>
      Import Bid:
      <Typography
        color={theme.palette.primary.main}
        fontWeight={theme.typography.fontWeightBold}
        variant={TYPOGRAPHY_VARIANTS.H6}
      >
        {planDetails.builderName} | {planDetails.series} | {planDetails.name}
      </Typography>
    </>
  );

  const allAreasReviewed = planDetails.planAreas?.every((area) => area.checked);
  const allOptionsReviewed = planDetails.options?.every((o) => o.reviewed);

  const ACTIVE_STEP_CONFIG = {
    [ACTIVE_STEP.CONFIGURE]: {
      title,
      nextLabel: "Configure Options",
      disabled: !allAreasReviewed,
      handleSubmit: (step) => {
        handleUpdateCurrentStep(planId, step);
        setActiveStep(step);
      },
    },
    [ACTIVE_STEP.OPTIONS]: {
      title: "Configure Options",
      nextLabel: "Review",
      disabled: !allOptionsReviewed,
      handleSubmit: (step) => {
        if (step > activeStep) {
          setContinueReviewModal(true);
        } else {
          handleUpdateCurrentStep(planId, step);
          setActiveStep(step);
        }
      },
    },
    [ACTIVE_STEP.REVIEW]: {
      title: "Review",
      nextLabel: "Finish",
      disabled: false,
      handleSubmit: (step) => {
        handleUpdateCurrentStep(planId, step);
        setActiveStep(step);
      },
    },
  };

  const selectedConfig = ACTIVE_STEP_CONFIG[activeStep] || {};

  return (
    <>
      <MiDetailPageHeader
        title={
          <Box display="flex" alignItems="center" gap={theme.spacing(2)}>
            <MiChip
              label={`${
                planDetails.communityId ? "Community Bid" : "Series Bid"
              } in ${
                planDetails.planStatus === "Draft" ? "Draft" : "Active"
              } Mode`}
              color="green"
              sx={{ borderRadius: 1 }}
            />
            {selectedConfig.title}
          </Box>
        }
        loading={isLoading}
        style={{ backgroundColor: theme.palette.green.light }}
      />
      <StepperView
        activeStep={activeStep}
        setActiveStep={selectedConfig.handleSubmit}
        disabled={selectedConfig.disabled}
        steps={ACTIVE_STEPS}
        nextLabel={selectedConfig.nextLabel}
        disableNavigation
      >
        {activeStep === ACTIVE_STEP.CONFIGURE && (
          <ImportPlanConfig
            planDetails={planDetails}
            areaTypes={areaTypes}
            stateManager={stateManager}
            setPlanDetails={setPlanDetails}
          />
        )}
        {activeStep === ACTIVE_STEP.OPTIONS && (
          <ImportPlanOptions
            planDetails={planDetails}
            stateManager={stateManager}
            setPlanDetails={setPlanDetails}
          />
        )}
      </StepperView>
      <MiModal
        open={continueReviewModal}
        title="All plan options have been reviewed"
        description="You have reviewed all sections for this Plan. Would you like to continue?"
        closeModal={() => setContinueReviewModal(false)}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            sx: { textDecoration: "underline" },
            color: BUTTON_COLORS.BLACK,
            onClick: () => {
              setContinueReviewModal(false);
            },
          },
          {
            name: "Yes, Continue",
            variant: BUTTON_VARIANTS.CONTAINED,
            onClick: () => {
              handleUpdateCurrentStep(planId, ACTIVE_STEP.REVIEW);
              setActiveStep(ACTIVE_STEP.REVIEW);
              setContinueReviewModal(false);
            },
          },
        ]}
      />
    </>
  );
}
